import { useState } from "react";

// ─── Google Material Symbols + DM Sans ───────────────────────────────────────
const ICON_LINK = "https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200&display=swap";
const FONT_LINK = "https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap";

const Icon = ({ name, size = 20, color, fill = 0, weight = 400, style = {} }: any) => (
  <span className="material-symbols-outlined" style={{
    fontSize: size, color, fontVariationSettings: `'FILL' ${fill}, 'wght' ${weight}, 'opsz' ${size}`,
    lineHeight: 1, verticalAlign: "middle", userSelect: "none", ...style,
  }}>{name}</span>
);

// ─── Maghil Design Tokens ────────────────────────────────────────────────────
const C = {
  bg: "#F5F6FA", surface: "#FFFFFF", card: "#FFFFFF", hover: "#F0F1F5",
  accent: "#D23A3B", accentSoft: "rgba(210,58,59,0.08)", accentGlow: "rgba(210,58,59,0.15)",
  green: "#16A34A", greenSoft: "rgba(22,163,74,0.08)",
  red: "#DC2626", redSoft: "rgba(220,38,38,0.08)",
  orange: "#D97706", orangeSoft: "rgba(217,119,6,0.08)",
  blue: "#2563EB", purple: "#7C3AED", teal: "#0D9488",
  t1: "#1A1D2E", t2: "#5A6178", t3: "#9CA3B8",
  cardBorder: "#E2E5EC", divider: "#E8EAF0",
  shadow: "0 1px 3px rgba(0,0,0,0.06)",
};

const CHANNEL_COLORS = [C.accent, C.green, C.orange, C.blue, C.purple, C.teal];

// ─── Mock Data ───────────────────────────────────────────────────────────────

const kpiRow1 = [
  { label: "NEW CUSTOMERS", value: "438", delta: "+12.4%", pos: true, icon: "person_add" },
  { label: "RETURNING", value: "2,942", delta: "+6.8%", pos: true, icon: "loyalty" },
  { label: "30D RETURN", value: "27.3%", delta: "+2.1%", pos: true, icon: "replay_30" },
];
const kpiRow2 = [
  { label: "CHURN RATE", value: "8.4%", delta: "-0.9%", pos: true, icon: "person_off" },
  { label: "AVG CLV", value: "$342", delta: "+$18", pos: true, icon: "diamond" },
  { label: "AT RISK", value: "356", delta: "+24", pos: false, icon: "warning" },
];

const rfmSegments = [
  { name: "Champions", count: 284, pct: 8.4, clv: "$1,240", avgVisits: 22, color: C.accent },
  { name: "Loyal Regulars", count: 612, pct: 18.1, clv: "$680", avgVisits: 14, color: C.green },
  { name: "Promising New", count: 438, pct: 13.0, clv: "$220", avgVisits: 3, color: C.blue },
  { name: "At Risk", count: 356, pct: 10.5, clv: "$520", avgVisits: 9, color: C.orange },
  { name: "Slipping Away", count: 520, pct: 15.4, clv: "$340", avgVisits: 6, color: "#D97706" },
  { name: "Hibernating", count: 680, pct: 20.1, clv: "$120", avgVisits: 2, color: C.t3 },
  { name: "Lost", count: 490, pct: 14.5, clv: "$85", avgVisits: 1, color: "#CBD5E1" },
];
const totalCustomers = rfmSegments.reduce((a, s) => a + s.count, 0);

const cohortData = [
  { cohort: "Sep '25", m0: 100, m1: 32, m2: 24, m3: 19, m4: 16, m5: 14 },
  { cohort: "Oct '25", m0: 100, m1: 29, m2: 22, m3: 18, m4: 15, m5: null },
  { cohort: "Nov '25", m0: 100, m1: 34, m2: 26, m3: 20, m4: null, m5: null },
  { cohort: "Dec '25", m0: 100, m1: 28, m2: 21, m3: null, m4: null, m5: null },
  { cohort: "Jan '26", m0: 100, m1: 31, m2: null, m3: null, m4: null, m5: null },
  { cohort: "Feb '26", m0: 100, m1: null, m2: null, m3: null, m4: null, m5: null },
];

const topCustomers = [
  { name: "Michael Chen", visits: 34, spend: "$4,820", lastVisit: "Mar 8", segment: "Champion", channel: "Dine-In" },
  { name: "Sarah Williams", visits: 28, spend: "$3,640", lastVisit: "Mar 9", segment: "Champion", channel: "Dine-In" },
  { name: "James Rodriguez", visits: 22, spend: "$2,860", lastVisit: "Mar 7", segment: "Loyal", channel: "Pickup" },
  { name: "Emily Davis", visits: 19, spend: "$2,470", lastVisit: "Mar 5", segment: "Loyal", channel: "Dine-In" },
  { name: "David Park", visits: 16, spend: "$2,080", lastVisit: "Mar 3", segment: "Loyal", channel: "Delivery" },
  { name: "Lisa Thompson", visits: 14, spend: "$1,820", lastVisit: "Feb 28", segment: "At Risk", channel: "Dine-In" },
  { name: "Robert Kim", visits: 12, spend: "$1,560", lastVisit: "Mar 6", segment: "Loyal", channel: "Pickup" },
  { name: "Anna Martinez", visits: 11, spend: "$1,430", lastVisit: "Mar 1", segment: "Promising", channel: "DoorDash" },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getCohortColor = (val: any) => {
  if (val === null || val === undefined) return C.hover;
  if (val >= 30) return C.green;
  if (val >= 20) return "rgba(22,163,74,0.55)";
  if (val >= 15) return "rgba(22,163,74,0.35)";
  if (val >= 10) return "rgba(22,163,74,0.2)";
  return "rgba(22,163,74,0.08)";
};

const SegmentBadge = ({ label, color }: any) => (
  <span style={{
    display: "inline-flex", alignItems: "center", gap: 3, padding: "2px 6px",
    borderRadius: 4, fontSize: 9, fontWeight: 600, color,
    background: `${color}14`,
  }}>
    <span style={{ width: 5, height: 5, borderRadius: 2, background: color }} />
    {label}
  </span>
);

// ─── Mobile Card Component ───────────────────────────────────────────────────

const MobileCard = ({ children, title, subtitle, icon, style = {}, drillable, onTap }: any) => (
  <div onClick={drillable ? onTap : undefined} style={{
    background: C.card, borderRadius: 14, border: `1px solid ${drillable ? `${C.accent}33` : C.cardBorder}`,
    boxShadow: C.shadow, padding: 14, position: "relative", ...style,
  }}>
    {drillable && (
      <div style={{ position: "absolute", top: 8, right: 8, opacity: 0.45 }}>
        <Icon name="chevron_right" size={14} color={C.accent} />
      </div>
    )}
    {title && (
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
        {icon && <Icon name={icon} size={15} color={C.t3} weight={400} />}
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.t1 }}>{title}</div>
          {subtitle && <div style={{ fontSize: 9, color: C.t3, marginTop: 1 }}>{subtitle}</div>}
        </div>
      </div>
    )}
    {children}
  </div>
);

// ─── Bottom Nav ──────────────────────────────────────────────────────────────

const BottomNav = ({ active }: any) => {
  const tabs = [
    { label: "Snapshot", icon: "electric_bolt" },
    { label: "Reports", icon: "bar_chart" },
    { label: "Brisque AI", icon: "auto_awesome" },
    { label: "Support", icon: "chat_bubble" },
  ];
  return (
    <div style={{
      height: 72, background: C.surface, borderTop: `1px solid ${C.divider}`,
      display: "flex", alignItems: "center", justifyContent: "space-around",
      paddingBottom: 8, position: "sticky", bottom: 0, zIndex: 20,
    }}>
      {tabs.map((tab, i) => {
        const isActive = tab.label === active;
        return (
          <div key={i} style={{
            display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
            padding: "4px 14px", cursor: "pointer", position: "relative",
          }}>
            <Icon name={tab.icon} size={20} color={isActive ? C.accent : C.t3}
              fill={isActive ? 1 : 0} weight={isActive ? 600 : 400}
              style={{ opacity: isActive ? 1 : 0.4 }} />
            <span style={{
              fontSize: 9.5, fontWeight: isActive ? 700 : 400,
              color: isActive ? C.accent : C.t3,
            }}>{tab.label}</span>
            {isActive && (
              <div style={{
                width: 4, height: 4, borderRadius: "50%", background: C.accent,
                position: "absolute", bottom: -2,
                boxShadow: `0 0 6px ${C.accentGlow}`,
              }} />
            )}
          </div>
        );
      })}
    </div>
  );
};

// ─── Date Picker (Mobile) ────────────────────────────────────────────────────

const MobileDatePicker = ({ value, onChange }: any) => {
  const presets = ["Today", "7D", "30D", "Month"];
  return (
    <div style={{ display: "flex", gap: 4, padding: "0 16px" }}>
      {presets.map(p => (
        <button key={p} onClick={() => onChange(p)} style={{
          flex: 1, padding: "5px 0", borderRadius: 7, fontSize: 10, fontWeight: 600,
          border: `1px solid ${value === p ? C.accent : C.divider}`,
          background: value === p ? C.accentSoft : C.surface,
          color: value === p ? C.accent : C.t3, cursor: "pointer",
        }}>{p}</button>
      ))}
    </div>
  );
};

// ─── Main Mobile Component ───────────────────────────────────────────────────

export default function CustomersReportMobile() {
  const [dateFilter, setDateFilter] = useState("30D");
  const [activeSegment, setActiveSegment] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState("");

  return (
    <>
      <link href={ICON_LINK} rel="stylesheet" />
      <link href={FONT_LINK} rel="stylesheet" />
      <div style={{
        fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        width: 375, height: 812, background: C.bg, color: C.t1,
        display: "flex", flexDirection: "column", overflow: "hidden",
        margin: "0 auto", borderRadius: 20, boxShadow: "0 0 40px rgba(0,0,0,0.12)",
        position: "relative",
      }}>

        {/* ════════════ STATUS BAR ════════════ */}
        <div style={{
          height: 44, background: C.surface, display: "flex", alignItems: "flex-end",
          justifyContent: "space-between", padding: "0 20px 4px",
        }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: C.t1 }}>9:41</span>
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            <Icon name="signal_cellular_alt" size={14} color={C.t1} fill={1} />
            <Icon name="wifi" size={14} color={C.t1} fill={1} />
            <Icon name="battery_full" size={14} color={C.t1} fill={1} />
          </div>
        </div>

        {/* ════════════ HEADER ════════════ */}
        <div style={{ background: C.surface, paddingBottom: 12 }}>
          {/* Title Row */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "8px 20px 6px",
          }}>
            <div>
              <div style={{ fontSize: 22, fontWeight: 700, color: C.t1 }}>Customers</div>
              <div style={{ fontSize: 11, color: C.t3, marginTop: 1 }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                  <Icon name="location_on" size={11} color={C.t3} />
                  Downtown Manhattan
                </span>
              </div>
            </div>
            <div style={{
              width: 30, height: 30, borderRadius: 8, background: C.accent,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#fff", fontSize: 11, fontWeight: 700,
            }}>JM</div>
          </div>

          {/* Date Picker */}
          <MobileDatePicker value={dateFilter} onChange={setDateFilter} />
        </div>

        {/* ════════════ SCROLLABLE CONTENT ════════════ */}
        <div style={{ flex: 1, overflowY: "auto", padding: "8px 16px 16px" }}>

          {/* ═══════ KPI Grid Row 1 (3 col) ═══════ */}
          <div style={{
            display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 8,
          }}>
            {kpiRow1.map((kpi, i) => (
              <div key={i} style={{
                background: C.card, borderRadius: 12, border: `1px solid ${C.cardBorder}`,
                boxShadow: C.shadow, padding: "12px 10px", textAlign: "center",
                position: "relative", overflow: "hidden",
              }}>
                <div style={{
                  position: "absolute", top: -10, right: -10, width: 40, height: 40,
                  borderRadius: "50%", background: kpi.pos ? C.greenSoft : C.redSoft,
                  filter: "blur(12px)", pointerEvents: "none",
                }} />
                <div style={{ fontSize: 8, fontWeight: 400, color: C.t3, letterSpacing: 0.3, textTransform: "uppercase" }}>
                  {kpi.label}
                </div>
                <div style={{ fontSize: 18, fontWeight: 700, color: C.t1, marginTop: 4 }}>{kpi.value}</div>
                <div style={{ fontSize: 9, fontWeight: 600, color: kpi.pos ? C.green : C.red, marginTop: 3, display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
                  <Icon name={kpi.pos ? "trending_up" : "trending_down"} size={10} color={kpi.pos ? C.green : C.red} />
                  {kpi.delta}
                </div>
              </div>
            ))}
          </div>

          {/* ═══════ KPI Grid Row 2 (3 col) ═══════ */}
          <div style={{
            display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 12,
          }}>
            {kpiRow2.map((kpi, i) => (
              <div key={i} style={{
                background: C.card, borderRadius: 12, border: `1px solid ${C.cardBorder}`,
                boxShadow: C.shadow, padding: "12px 10px", textAlign: "center",
                position: "relative", overflow: "hidden",
              }}>
                <div style={{
                  position: "absolute", top: -10, right: -10, width: 40, height: 40,
                  borderRadius: "50%", background: kpi.pos ? C.greenSoft : C.redSoft,
                  filter: "blur(12px)", pointerEvents: "none",
                }} />
                <div style={{ fontSize: 8, fontWeight: 400, color: C.t3, letterSpacing: 0.3, textTransform: "uppercase" }}>
                  {kpi.label}
                </div>
                <div style={{ fontSize: 18, fontWeight: 700, color: C.t1, marginTop: 4 }}>{kpi.value}</div>
                <div style={{ fontSize: 9, fontWeight: 600, color: kpi.pos ? C.green : C.red, marginTop: 3, display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
                  <Icon name={kpi.pos ? "trending_up" : "trending_down"} size={10} color={kpi.pos ? C.green : C.red} />
                  {kpi.delta}
                </div>
              </div>
            ))}
          </div>

          {/* ═══════ Churn Alert Banner ═══════ */}
          <div style={{
            background: C.orangeSoft, border: `1px solid ${C.orange}33`, borderRadius: 12,
            padding: "10px 12px", display: "flex", gap: 10, alignItems: "flex-start",
            marginBottom: 12,
          }}>
            <Icon name="warning" size={16} color={C.orange} fill={1} style={{ marginTop: 1, flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: C.t1 }}>356 customers at risk</div>
              <div style={{ fontSize: 9, color: C.t2, marginTop: 2, lineHeight: 1.4 }}>
                +24 vs prior. Top: Lisa Thompson — 14 visits, $1,820 CLV, 10 days overdue.
              </div>
            </div>
            <Icon name="chevron_right" size={14} color={C.orange} style={{ marginTop: 2, flexShrink: 0 }} />
          </div>

          {/* ═══════ RFM Segments Card ═══════ */}
          <MobileCard title="Customer Segments" subtitle={`${totalCustomers.toLocaleString()} identified`} icon="pie_chart"
            style={{ marginBottom: 8 }}>
            {/* Visual blocks */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 10 }}>
              {rfmSegments.map((seg, i) => {
                const w = Math.max(42, Math.sqrt(seg.count) * 3.2);
                const isActive = activeSegment === seg.name;
                return (
                  <div key={i} onClick={() => setActiveSegment(isActive ? null : seg.name)} style={{
                    width: w, height: w, borderRadius: 8, cursor: "pointer",
                    background: isActive ? `${seg.color}28` : `${seg.color}14`,
                    border: `1.5px solid ${isActive ? seg.color : `${seg.color}30`}`,
                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                    padding: 3, transition: "all 0.15s",
                  }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: seg.color }}>{seg.count}</div>
                    <div style={{ fontSize: 6.5, fontWeight: 600, color: seg.color, textAlign: "center", lineHeight: 1.1, marginTop: 1 }}>
                      {seg.name}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Segment list */}
            {rfmSegments.map((seg, i) => (
              <div key={i} onClick={() => setActiveSegment(activeSegment === seg.name ? null : seg.name)}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  borderBottom: i < rfmSegments.length - 1 ? `1px solid ${C.divider}` : "none",
                  cursor: "pointer",
                  background: activeSegment === seg.name ? C.hover : "transparent",
                  margin: activeSegment === seg.name ? "0 -14px" : 0,
                  padding: activeSegment === seg.name ? "6px 14px" : "6px 0",
                  borderRadius: activeSegment === seg.name ? 6 : 0,
                }}>
                <span style={{ width: 6, height: 6, borderRadius: 2, background: seg.color, flexShrink: 0 }} />
                <span style={{ fontSize: 11, color: C.t2, flex: 1 }}>{seg.name}</span>
                <span style={{ fontSize: 10, fontWeight: 600, color: C.t1, width: 32, textAlign: "right" }}>{seg.count}</span>
                <span style={{ fontSize: 9, color: C.t3, width: 28, textAlign: "right" }}>{seg.pct}%</span>
                <span style={{ fontSize: 9, color: C.t3, width: 38, textAlign: "right" }}>{seg.clv}</span>
              </div>
            ))}
          </MobileCard>

          {/* ═══════ Cohort Retention Heatmap ═══════ */}
          <MobileCard title="Cohort Retention" subtitle="Monthly return rates" icon="grid_on"
            style={{ marginBottom: 8 }}>
            <div style={{ overflowX: "auto", margin: "0 -6px" }}>
              <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 2, minWidth: 310 }}>
                <thead>
                  <tr>
                    <th style={{ fontSize: 8, fontWeight: 600, color: C.t3, textAlign: "left", padding: "2px 4px", width: 52 }}>COHORT</th>
                    {["M0", "M1", "M2", "M3", "M4", "M5"].map(m => (
                      <th key={m} style={{ fontSize: 7, fontWeight: 500, color: C.t3, textAlign: "center", padding: "2px 2px" }}>{m}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cohortData.map((row, ri) => (
                    <tr key={ri}>
                      <td style={{ fontSize: 9, fontWeight: 500, color: C.t2, padding: "2px 4px" }}>{row.cohort}</td>
                      {[row.m0, row.m1, row.m2, row.m3, row.m4, row.m5].map((val, ci) => (
                        <td key={ci} style={{ textAlign: "center", padding: 0 }}>
                          <div style={{
                            background: getCohortColor(val), borderRadius: 3,
                            padding: "5px 2px", fontSize: 9, fontWeight: val !== null ? 600 : 400,
                            color: val !== null ? (val >= 30 ? "#fff" : C.t1) : C.t3,
                          }}>
                            {val !== null ? `${val}%` : "—"}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Legend */}
            <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
              {[
                { label: "30%+", bg: C.green },
                { label: "20–30", bg: "rgba(22,163,74,0.55)" },
                { label: "15–20", bg: "rgba(22,163,74,0.35)" },
                { label: "10–15", bg: "rgba(22,163,74,0.2)" },
                { label: "<10", bg: "rgba(22,163,74,0.08)" },
              ].map(l => (
                <div key={l.label} style={{ display: "flex", alignItems: "center", gap: 3 }}>
                  <span style={{ width: 10, height: 7, borderRadius: 2, background: l.bg }} />
                  <span style={{ fontSize: 7, color: C.t3 }}>{l.label}</span>
                </div>
              ))}
            </div>
          </MobileCard>

          {/* ═══════ Top Customers List ═══════ */}
          <MobileCard title="Top Customers" subtitle="Highest lifetime spend" icon="workspace_premium"
            style={{ marginBottom: 8 }}>
            {/* Search */}
            <div style={{
              display: "flex", alignItems: "center", gap: 6, padding: "6px 10px",
              background: C.hover, borderRadius: 8, marginBottom: 10,
            }}>
              <Icon name="search" size={14} color={C.t3} />
              <input
                placeholder="Search..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                style={{
                  border: "none", outline: "none", background: "transparent",
                  fontSize: 11, color: C.t1, width: "100%",
                  fontFamily: "'DM Sans', sans-serif",
                }}
              />
            </div>

            {/* Customer cards */}
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              {topCustomers
                .filter(c => !searchQuery || c.name.toLowerCase().includes(searchQuery.toLowerCase()))
                .map((c, i) => {
                  const seg = rfmSegments.find(s => s.name === c.segment || s.name.startsWith(c.segment));
                  return (
                    <div key={i} style={{
                      background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 10,
                      padding: "10px 12px", boxShadow: C.shadow,
                    }}>
                      {/* Row 1: Rank + Name + Spend */}
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{
                          display: "inline-flex", alignItems: "center", justifyContent: "center",
                          width: 18, height: 18, borderRadius: 4, fontSize: 9, fontWeight: 700,
                          background: i === 0 ? C.accentSoft : C.hover,
                          color: i === 0 ? C.accent : C.t3, flexShrink: 0,
                        }}>{i + 1}</span>
                        <div style={{
                          width: 26, height: 26, borderRadius: "50%", flexShrink: 0,
                          background: `${CHANNEL_COLORS[i % CHANNEL_COLORS.length]}18`,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 9, fontWeight: 600, color: CHANNEL_COLORS[i % CHANNEL_COLORS.length],
                        }}>
                          {c.name.split(" ").map(n => n[0]).join("")}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: C.t1 }}>{c.name}</div>
                        </div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: C.t1, flexShrink: 0 }}>{c.spend}</div>
                      </div>

                      {/* Row 2: Pills */}
                      <div style={{ display: "flex", gap: 5, marginTop: 6, marginLeft: 52, flexWrap: "wrap" }}>
                        <SegmentBadge label={c.segment} color={seg?.color || C.t3} />
                        <span style={{
                          fontSize: 9, color: C.t3, padding: "2px 6px",
                          background: C.hover, borderRadius: 4, display: "inline-flex", alignItems: "center", gap: 3,
                        }}>
                          <Icon name="replay" size={9} color={C.t3} />{c.visits} visits
                        </span>
                        <span style={{
                          fontSize: 9, color: C.t3, padding: "2px 6px",
                          background: C.hover, borderRadius: 4, display: "inline-flex", alignItems: "center", gap: 3,
                        }}>
                          <Icon name="schedule" size={9} color={C.t3} />{c.lastVisit}
                        </span>
                        <span style={{
                          fontSize: 9, color: C.t3, padding: "2px 6px",
                          background: C.hover, borderRadius: 4, display: "inline-flex", alignItems: "center", gap: 3,
                        }}>
                          <Icon name={c.channel === "Dine-In" ? "restaurant" : c.channel === "Pickup" ? "shopping_bag" : c.channel === "Delivery" ? "local_shipping" : "phone_android"} size={9} color={C.t3} />
                          {c.channel}
                        </span>
                      </div>
                    </div>
                  );
                })}
            </div>
          </MobileCard>

          {/* Bottom spacer for safe area */}
          <div style={{ height: 8 }} />
        </div>

        {/* ════════════ BOTTOM NAV ════════════ */}
        <BottomNav active="Reports" />
      </div>
    </>
  );
}
